import React, { useState, useEffect, useRef } from "react";

interface IdentityChipProps {
  label: string;
  onLogout: () => void;
  onSwitchUser: () => void;
}

export default function IdentityChip({
  label,
  onLogout,
  onSwitchUser,
}: IdentityChipProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="position-relative d-inline-block"
      style={{ zIndex: 10 }}
    >
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        aria-label={label}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{label}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="dropdown-menu show position-absolute end-0 mt-1"
          style={{ minWidth: "10rem" }}
        >
          <button
            type="button"
            role="menuitem"
            className="dropdown-item"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            Log out
          </button>
          <button
            type="button"
            role="menuitem"
            className="dropdown-item"
            onClick={() => {
              setOpen(false);
              onSwitchUser();
            }}
          >
            Switch user
          </button>
        </div>
      )}
    </div>
  );
}
